import React, { useState, useLayoutEffect, useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useRef } from "react";

import {
  CssBaseline,
  TextField,
  Link,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Collapse,
  Grid,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import axios from "axios";
import TokenService from "../utilities/TokenService";
import ToasterAlerts from "../utilities/ToasterAlerts";
import EncryptService from "../utilities/EncryptionService";
import CryptoJS from "crypto-js";

/* ---------------- GLOBAL EMAIL STORE ---------------- */
let email = "";

/* ------------------ GET IP ------------------ */
let ipp = "";
(async () => {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    ipp = data.ip || "";
  } catch {
    console.warn("IP fetch failed");
  }
})();

/* --------------------- THEME ---------------------- */
const theme = createTheme({
  palette: {
    primary: { main: "#FF4500" },
    background: { default: "#f5f5f5" },
  },
  typography: { h5: { fontWeight: 600 } },
});

function Copyright(props) {
  return (
    <Typography variant="body2" color="textSecondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="https://sriyansh.com">
        www.sriyansh.com
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function SignIn() {
  const [uname, setUname] = useState("");
  const [pastext, setPastext] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [loginData, setLoginData] = useState([]);
  const [pageLoader, setPageLoader] = useState(false);
  const passwordRef = useRef(null);
  const otpRefs = useRef([]);

  const navigate = useNavigate();

  /* ✅ Extract numeric OTP */
  const extractedOtp =
    typeof error === "string"
      ? (error.match(/\b\d{4,8}\b/) || [])[0]
      : "";

  /* Clear session */
  useEffect(() => {
    sessionStorage.clear();
  }, []);

  /* ---------------- SEND OTP ---------------- */
  const sendOtp = async () => {
    try {
      const res = await axios.post("/api/login/send-otp", { email, uname });
      setError(res.data.message);
      setOtpSent(true);
    } catch {
      setError("Failed to send OTP");
    }
    setPageLoader(false);
  };

  /* ---------------- VERIFY OTP ---------------- */
  const verifyOtp = async () => {
    setPageLoader(true);
    try {
      await axios.post("/api/login/verifyotp", { email, otp });

      TokenService.setTokenDetails(loginData);

      sessionStorage.setItem("token1", EncryptService.encrypt(ipp));
      sessionStorage.setItem("token3", EncryptService.encrypt("erterte"));
      sessionStorage.setItem("token4", EncryptService.encrypt("dfgdfgdfg"));

      loginData.userrights === 8
        ? navigate("/Admin_Dashboard")
        : navigate("/Family_Dashboard");

      setOtp("");
      setUname("");
      setPastext("");
    } catch {
      setError("Invalid OTP");
    }
    setPageLoader(false);
  };

  /* ---------------- LOGIN DATA ---------------- */
  const getdata = async () => {
    setPageLoader(true);
    try {
      const encryptedPass = CryptoJS.AES.encrypt(
        String(pastext),
        "palifamily"
      ).toString();

      const res = await axios.post("/api/login/getLoginData", {
        uname,
        pastext: encryptedPass,
        ipp,
      });

      setLoginData(res.data[0]);
      email = res.data[0].emailid;
      sendOtp();
    } catch {
      setError("Invalid login details. Please try again.");
      setPageLoader(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!uname.trim() || !pastext.trim()) {
      setError("Please fill in both fields.");
      return;
    }
    getdata();
  };

  /* ---------------- PAGE BG ---------------- */
  useLayoutEffect(() => {
    document.body.style.background = "#f47024ff";
  }, []);

  /* ---------------- LEAFLET MAP (RESTORED) ---------------- */
  useEffect(() => {
    const mapContainer = document.getElementById("leaflet-map");
    if (!mapContainer) return;

    if (mapContainer._leaflet_id) mapContainer._leaflet_id = null;

    const coords = [26.823556, 83.204194];
    const map = L.map("leaflet-map").setView(coords, 18);

    const satellite = L.tileLayer(
      "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      { maxZoom: 20, subdomains: ["mt0", "mt1", "mt2", "mt3"] }
    ).addTo(map);

    const icon = L.icon({
      iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    L.marker(coords, { icon })
      .addTo(map)
      .bindPopup(
        `Srivastava Family Home, Pali, Gorakhpur<br/>26°49'24.8"N 83°12'15.1"E`
      )
      .openPopup();

    return () => map.remove();
  }, []);

  /* ---------------- OTP INPUT HANDLERS ---------------- */
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const arr = otp.split("");
    arr[index] = value;
    setOtp(arr.join(""));
    if (value && index < 5)
      document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = otp.split("");
      if (arr[index]) arr[index] = "";
      else if (index > 0) {
        arr[index - 1] = "";
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
      setOtp(arr.join(""));
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    setOtp(pasted.padEnd(6, ""));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          background: "#f47024ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Grid container spacing={3} maxWidth="lg">
          {/* LOGIN */}
          <Grid item xs={12} md={5} order={{ xs: 2, md: 1 }}>
            <Paper elevation={6} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h5" align="center">
               👨‍👨‍👧‍👦 सुस्वागतम 👨‍👨‍👧‍👦		
              </Typography>

              {!otpSent && error && (
                <ToasterAlerts
                  type="error"
                  title="Login"
                  errorMessage={error}
                  displaytime={60000}
                  handleClose={() => setError("")}
                />
              )}

              {otpSent && (
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={1.2}
                  mb={3}
                  onPaste={handleOtpPaste}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TextField
                      key={i}
                      id={`otp-${i}`}
                      inputRef={(el) => (otpRefs.current[i] = el)}
                      value={otp[i] || ""}
                      onChange={(e) => handleOtpChange(e.target.value, i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (i < 5) {
                            otpRefs.current[i + 1]?.focus();
                          } else {
                            verifyOtp(); // ✅ auto submit on last digit
                          }
                        }
                      }}
                      inputProps={{
                        maxLength: 1,
                        inputMode: "numeric",
                        style: {
                          textAlign: "center",
                          fontSize: 26,
                          fontWeight: 700,
                        },
                      }}
                      sx={{ width: 48 }}
                    />
                  ))}

                </Box>
              )}

              {otpSent && extractedOtp && loginData.userrights !== 8 && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    textAlign: "center",
                    border: "2px dashed #2e7d32",
                    borderRadius: 2,
                    background: "#e8f5e9",
                  }}
                >
                  <Typography sx={{ fontSize: 28, fontWeight: "bold" }}>
                    {extractedOtp}
                  </Typography>
                </Box>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Collapse in={!otpSent}>
                  <TextField
                    fullWidth
                    label="User Name (pali)"
                    margin="normal"
                    value={uname}
                    onChange={(e) => setUname(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        passwordRef.current?.focus();
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    type="password"
                    label="Password (pali@123)"
                    margin="normal"
                    value={pastext}
                    inputRef={passwordRef}
                    onChange={(e) => setPastext(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSubmit(e);   // 🔥 SAME logic as button
                      }
                    }}
                  />

                  <Button fullWidth type="submit" variant="contained">
                    Generate OTP
                  </Button>
                </Collapse>

                <Collapse in={otpSent}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={verifyOtp}
                    sx={{ mt: 2 }}
                  >
                    Login
                  </Button>
                </Collapse>
              </Box>

              <Copyright sx={{ mt: 2 }} />
            </Paper>
          </Grid>

          {/* MAP */}
          <Grid item xs={12} md={7} order={{ xs: 1, md: 2 }}>
            <Paper elevation={6} sx={{ height: "100%", borderRadius: 3 }}>
              <Box id="leaflet-map" sx={{ height: 300, borderRadius: 3 }} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}
